import {
    FunctionComponent,
    useState,
    createContext,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import {
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { SbSharedThinBrowseItemTrackingProvider_items$data } from './__generated__/SbSharedThinBrowseItemTrackingProvider_items.graphql';
import { SbSharedThinBrowseItemTrackingProvider_itemSearch$data } from './__generated__/SbSharedThinBrowseItemTrackingProvider_itemSearch.graphql';
import { SbSharedThinBrowseItemTrackingProvider_viewer$data } from './__generated__/SbSharedThinBrowseItemTrackingProvider_viewer.graphql';

type ThinBrowseItemTrackingContextType = {
    fireThinBrowseItemsImpressionTracking: (index: number) => void;
    fireItemClickTracking: (index: number) => void;
};

export const SbSharedThinBrowseItemTrackingContext = createContext<ThinBrowseItemTrackingContextType>(
    {
        fireThinBrowseItemsImpressionTracking: () => {},
        fireItemClickTracking: () => {},
    }
);

type Props = {
    children: ReactNode;
    thinBrowseListingType: string;
    itemSearch: SbSharedThinBrowseItemTrackingProvider_itemSearch$data;
    items: SbSharedThinBrowseItemTrackingProvider_items$data;
    viewer: SbSharedThinBrowseItemTrackingProvider_viewer$data;
};

const SbSharedThinBrowseItemTrackingProviderComponent: FunctionComponent<Props> = ({
    children,
    itemSearch,
    items,
    thinBrowseListingType,
    viewer,
}) => {
    const [hasTrackedRecentlySoldItems, setHasTrackedRecentlySoldItems] = useState(false);
    const [attemptedItemImpression, setAttemptedItemImpression] = useState(false);

    const userRegions = useCallback(
        () => getUserSessionRegions() || viewer?.regionalInfo?.[0]?.regionsByZipCode || [],
        [viewer]
    );
    const regionalInfoAvailable = useCallback(
        () => !!(getUserSessionRegions() || viewer?.regionalInfo),
        [viewer]
    );

    const fireThinBrowseItemsImpressionTracking = useCallback(
        index => {
            if (!regionalInfoAvailable) {
                setAttemptedItemImpression(true);
            } else if (!hasTrackedRecentlySoldItems && index === 0) {
                const { searchTerm } = itemSearch || {};

                trackEcommerceProductImpressions({
                    items: items.map(item => ({
                        node: item,
                    })),
                    pageType: thinBrowseListingType,
                    startIndex: index,
                    searchTerm,
                    userRegions: userRegions(),
                });
                setHasTrackedRecentlySoldItems(true);
            }
        },
        [
            hasTrackedRecentlySoldItems,
            itemSearch,
            items,
            regionalInfoAvailable,
            thinBrowseListingType,
            userRegions,
        ]
    );

    const fireItemClickTracking = (index: number): void => {
        const { searchTerm } = itemSearch || {};
        const item = items[index];

        trackEcommerceProductClick({
            item,
            index,
            pageType: thinBrowseListingType,
            searchTerm,
            userRegions: userRegions(),
        });
    };

    useEffect(() => {
        if (regionalInfoAvailable() && attemptedItemImpression) {
            fireThinBrowseItemsImpressionTracking(0);
            setAttemptedItemImpression(false);
        }
    }, [attemptedItemImpression, fireThinBrowseItemsImpressionTracking, regionalInfoAvailable]);

    return (
        <SbSharedThinBrowseItemTrackingContext.Provider
            value={{
                fireThinBrowseItemsImpressionTracking,
                fireItemClickTracking,
            }}
        >
            {children}
        </SbSharedThinBrowseItemTrackingContext.Provider>
    );
};

export const SbSharedThinBrowseItemTrackingProvider = createFragmentContainer(
    SbSharedThinBrowseItemTrackingProviderComponent,
    {
        itemSearch: graphql`
            fragment SbSharedThinBrowseItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
                searchTerm
            }
        `,
        items: graphql`
            fragment SbSharedThinBrowseItemTrackingProvider_items on Item @relay(plural: true) {
                serviceId
                title
                categoryPath
                browseUrl
                categoryCode
                contemporaryTrackingString
                seller {
                    serviceId
                }
                displayPriceTracking: displayPrice(page: searchAndBrowse, isTrade: $isTrade) {
                    convertedAmountList {
                        amount
                        currency
                    }
                    amountType
                    textType
                }
                isAuctionEnabled
                ...freeShippingHelpers_item
            }
        `,
        viewer: graphql`
            fragment SbSharedThinBrowseItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
    }
);
